import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import { convertLexicalToHTML } from '@payloadcms/richtext-lexical/html'
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical'
import { cn } from '../helpers/index.js'
import type { ComponentPropsWithout, RemovedProps } from '../helpers/component-props.js'

const textVariants = cva('text-pretty tracking-[-0.02em]', {
  variants: {
    size: {
      none: 'tracking',
      1: 'text-[12px] leading-[140%]',
      2: 'text-[14px] leading-[140%]',
      3: 'text-[16px] leading-[140%]',
      4: 'text-[18px] leading-[140%]',
    },
    weight: {
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
      extraBold: 'font-extrabold',
      black: 'font-black',
    },
    align: {
      left: 'text-left',
      center: 'text-center',
      right: 'text-right',
    },
  },
  defaultVariants: {
    size: 'none',
    weight: 'bold',
  },
})

type TextElement = React.ElementRef<'span'>
type TextSpanProps = { as?: 'span' } & ComponentPropsWithout<'span', RemovedProps>
type TextDivProps = { as: 'div' } & ComponentPropsWithout<'div', RemovedProps>
type TextLabelProps = { as: 'label' } & ComponentPropsWithout<'label', RemovedProps>
type TextPProps = { as: 'p' } & ComponentPropsWithout<'p', RemovedProps>

interface CommonTextProps extends VariantProps<typeof textVariants> {
  as?: 'div' | 'label' | 'p' | 'span'
  asChild?: boolean
  color?: string
  /** 相当于 dangerouslySetInnerHTML */
  html?: string | SerializedEditorState<SerializedLexicalNode>
}

type TextProps = CommonTextProps & (TextSpanProps | TextDivProps | TextLabelProps | TextPProps)

/**
 * Text - 文本组件
 *
 * @description 用于显示文本内容的组件，支持多种尺寸、字重、对齐方式和HTML内容
 *
 * @param {object} props - 组件属性
 * @param {1|2|3|4|"none"} props.size - 文字尺寸
 * - `1`: 12px (小号文字，适用于说明文字)
 * - `2`: 14px (常规文字，适用于正文内容)
 * - `3`: 16px (中号文字，适用于重要内容)
 * - `4`: 18px (大号文字，适用于标题或强调内容)
 * - `none`: 无预设大小，继承父级样式
 * @param {"medium"|"semibold"|"bold"|"extraBold"|"black"} props.weight - 字重
 * @param {"left"|"center"|"right"} props.align - 文字对齐方式
 * @param {"div"|"label"|"p"|"span"} props.as - 渲染的 HTML 标签
 * @param {boolean} props.asChild - 是否作为子组件渲染
 * @param {string} props.color - 文字颜色
 * @param {string|SerializedEditorState} props.html - HTML 内容或富文本编辑器状态
 *
 * @example
 * ```tsx
 * // 基础用法
 * <Text size={2}>这是一段正文</Text>
 *
 * // 大号加粗文字
 * <Text size={4} weight="bold">重要标题</Text>
 *
 * // 居中对齐的小号文字
 * <Text size={1} align="center">说明文字</Text>
 *
 * // 渲染为段落标签
 * <Text as="p" size={3}>段落内容</Text>
 * ```
 */
const Text = React.forwardRef<TextElement, TextProps>((props, forwardedRef) => {
  const {
    children,
    size = 'none', // 为了兼容旧版本样式，默认值为 none，没有默认的字体大小
    weight,
    align,
    className,
    asChild,
    as: Tag = 'span',
    color,
    html,
    ...textProps
  } = props

  const htmlContentProps = html
    ? {
        dangerouslySetInnerHTML: { __html: typeof html === 'string' ? html : convertLexicalToHTML({ data: html }) },
      }
    : null

  const styleProps = color
    ? {
        style: {
          color,
        },
      }
    : null

  return (
    <Slot
      {...textProps}
      ref={forwardedRef}
      className={cn(
        textVariants({
          size,
          weight,
          align,
        }),
        className
      )}
      {...htmlContentProps}
      {...styleProps}
    >
      {asChild ? children : <Tag>{htmlContentProps ? null : children}</Tag>}
    </Slot>
  )
})
Text.displayName = 'Text'

export { Text }
export type { TextProps }
